import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { countWords, mergeSeries } from '../lib/format';
import { isVideoPost } from '../../utils/video';

const SiteDataContext = createContext(null);

const stripHeavyFields = (post) => {
    const { content, bibliography, comments, ...rest } = post || {};
    return {
        ...rest,
        wordCount: Number.isFinite(post?.wordCount)
            ? post.wordCount
            : countWords(content || ''),
    };
};

// Preferred source: the lightweight /api/posts/index endpoint. Older backend
// deployments may not have it yet, so fall back to paging the full list.
const fetchAllPosts = async () => {
    try {
        const response = await axios.get(`${API_URL}/api/posts/index`);
        if (!Array.isArray(response.data?.posts)) {
            // Hosts with an SPA catch-all answer missing API routes with
            // 200 + index.html, which axios won't reject — force the
            // fallback instead of silently rendering an empty site.
            throw new Error('posts index endpoint unavailable');
        }
        return response.data.posts.map(stripHeavyFields);
    } catch (error) {
        const collected = [];
        let page = 1;
        let totalPages = 1;

        do {
            const response = await axios.get(`${API_URL}/api/posts?page=${page}&limit=100`);
            const posts = Array.isArray(response.data?.posts) ? response.data.posts : [];
            collected.push(...posts.map(stripHeavyFields));
            totalPages = Number(response.data?.totalPages) || 1;
            page += 1;
        } while (page <= totalPages);

        return collected;
    }
};

// Writer-created series. Optional: a failure never breaks the site, it just
// means no explicit series. It resolves to null then (seriesStatus 'error'),
// so a series page can tell "not loaded" from "no such series". A non-list
// answer (an older backend's SPA fallback page) means there are none.
const fetchExplicitSeries = async () => {
    try {
        const response = await axios.get(`${API_URL}/api/series`);
        return Array.isArray(response.data?.series) ? response.data.series : [];
    } catch {
        return null;
    }
};

const sortNewestFirst = (posts) => (
    [...posts].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
);

export function SiteDataProvider({ children }) {
    const [posts, setPosts] = useState([]);
    const [groups, setGroups] = useState([]);
    const [explicitSeries, setExplicitSeries] = useState([]);
    const [status, setStatus] = useState('loading');
    const [seriesStatus, setSeriesStatus] = useState('loading');

    // Lets ArticlePage feed its freshly-fetched post back in, so posts
    // missing from (or stale in) the startup index still get correct
    // series/adjacency data and SEO meta.
    const upsertPost = useCallback((post) => {
        if (!post || !post._id) {
            return;
        }
        setPosts((previous) => {
            const existing = previous.find((entry) => entry._id === post._id);
            if (existing && String(existing.updatedAt) === String(post.updatedAt)) {
                return previous;
            }
            return sortNewestFirst([
                ...previous.filter((entry) => entry._id !== post._id),
                stripHeavyFields(post),
            ]);
        });
    }, []);

    useEffect(() => {
        let active = true;

        const load = async () => {
            try {
                const [allPosts, tagsResponse, seriesList] = await Promise.all([
                    fetchAllPosts(),
                    axios.get(`${API_URL}/api/posts/tags`),
                    fetchExplicitSeries(),
                ]);

                if (!active) {
                    return;
                }

                const rawGroups = Array.isArray(tagsResponse.data?.groups)
                    ? tagsResponse.data.groups
                    : [];

                setPosts(sortNewestFirst(allPosts));
                setGroups(rawGroups);
                setExplicitSeries(seriesList || []);
                setSeriesStatus(seriesList ? 'ready' : 'error');
                setStatus('ready');
            } catch (error) {
                console.error('[ERROR] Failed to load site data:', error);
                if (active) {
                    setStatus('error');
                }
            }
        };

        load();

        return () => {
            active = false;
        };
    }, []);

    const value = useMemo(() => {
        // Condoned Media (video) posts live on /videos; `articles` is
        // everything else (both newest-first). Categories and their counts
        // describe articles only; tags, search and series use all posts.
        const articles = posts.filter((post) => !isVideoPost(post));
        const mediaPosts = posts.filter(isVideoPost);

        const subjectGroups = groups.filter((group) => (group?.category || 'subjects') === 'subjects');

        const matchesGroup = (post, subTagSet) => (
            Array.isArray(post?.tags)
            && post.tags.some((tag) => subTagSet.has(String(tag || '').toLowerCase()))
        );

        const categories = subjectGroups
            .map((group) => {
                const subTagSet = new Set(
                    (Array.isArray(group.subTags) ? group.subTags : [])
                        .map((tag) => String(tag || '').toLowerCase())
                );
                return {
                    id: group.id,
                    label: group.label,
                    subTagSet,
                    count: articles.filter((post) => matchesGroup(post, subTagSet)).length,
                };
            })
            .filter((category) => category.count > 0);

        // Posts whose tags all live in non-subject groups (geography/persons)
        // fall into a synthetic "Uncategorized" category, mirroring the live
        // site where every post always has a category.
        const isUncategorized = (post) => (
            !categories.some((category) => matchesGroup(post, category.subTagSet))
        );
        const uncategorizedCount = articles.filter(isUncategorized).length;
        if (uncategorizedCount > 0) {
            categories.push({
                id: 'uncategorized',
                label: 'Uncategorized',
                subTagSet: new Set(),
                count: uncategorizedCount,
                fallback: true,
            });
        }

        // Video posts belong to Condoned Media, not to an article category
        // (a category page would not list them).
        const getPostCategory = (post) => {
            if (!post || !Array.isArray(post.tags) || isVideoPost(post)) {
                return null;
            }
            return categories.find((category) => (
                category.fallback ? isUncategorized(post) : matchesGroup(post, category.subTagSet)
            )) || null;
        };

        const getCategoryById = (id) => categories.find((category) => category.id === id) || null;

        const getPostsForCategory = (category) => {
            if (!category) {
                return [];
            }
            if (category.fallback) {
                return articles.filter(isUncategorized);
            }
            return articles.filter((post) => matchesGroup(post, category.subTagSet));
        };

        // Writer-created + title-detected series (explicit wins), over all posts.
        const series = mergeSeries(explicitSeries, posts);

        const getSeriesForPost = (post) => {
            if (!post?._id) {
                return null;
            }
            return series.find((entry) => entry.parts.some((part) => part.postId === post._id)) || null;
        };

        const getAdjacentPosts = (postId) => {
            const current = posts.find((post) => post._id === postId);
            if (!current) {
                return { previous: null, next: null };
            }
            // Stay within the same kind: articles step through articles,
            // Condoned Media posts through other videos.
            const pool = isVideoPost(current) ? mediaPosts : articles;
            const index = pool.findIndex((post) => post._id === postId);
            return {
                // pools are newest-first: "previous" = older, "next" = newer
                previous: pool[index + 1] || null,
                next: pool[index - 1] || null,
            };
        };

        return {
            status,
            seriesStatus,
            posts,
            articles,
            mediaPosts,
            groups,
            categories,
            series,
            getPostCategory,
            getCategoryById,
            getPostsForCategory,
            getSeriesForPost,
            getAdjacentPosts,
            upsertPost,
        };
    }, [posts, groups, explicitSeries, status, seriesStatus, upsertPost]);

    return (
        <SiteDataContext.Provider value={value}>
            {children}
        </SiteDataContext.Provider>
    );
}

export const useSiteData = () => {
    const context = useContext(SiteDataContext);
    if (!context) {
        throw new Error('useSiteData must be used within a SiteDataProvider');
    }
    return context;
};
